import axios from 'axios';  
// Create axios instance — cookies are sent automatically via withCredentials
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1',
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true  // Required so httpOnly cookies are included in cross-origin requests
});

// Request interceptor to specify portal origin
apiClient.interceptors.request.use(
  (config) => {
    config.headers['X-Portal-Source'] = 'admin';
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Token refresh state ──────────────────────────────────────────────────────
// Prevents multiple simultaneous refresh calls (request queuing)
let isRefreshing = false;
let pendingRequests = [];

const processQueue = (error) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });
  pendingRequests = [];
};

// ── Codes that should trigger a silent token refresh attempt ─────────────────
// TOKEN_EXPIRED : JWT is present but expired (edge case: tiny clock skew)
// NO_TOKEN      : Cookie was auto-deleted by browser when it hit maxAge expiry
//                 (this is the NORMAL real-world expiry path — browser removes
//                  the cookie before the request is sent, so backend sees nothing)
const REFRESH_TRIGGER_CODES = ['TOKEN_EXPIRED', 'NO_TOKEN'];

// ── Response interceptor ─────────────────────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const errorCode = error.response?.data?.error?.code;
    const status    = error.response?.status;

    // Attempt silent refresh for any recoverable 401
    // Guard _retried to prevent infinite loops
    if (status === 401 && REFRESH_TRIGGER_CODES.includes(errorCode) && !originalRequest._retried) {
      originalRequest._retried = true;

      if (isRefreshing) {
        // Another request already kicked off a refresh — queue this one
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // Sends the adminRefreshToken httpOnly cookie automatically
        await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/auth/admin/refresh-token`,
          {},
          { withCredentials: true }
        );

        processQueue(null);
        isRefreshing = false;

        // Retry the original request — new adminAccessToken cookie is now set
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        isRefreshing = false;

        // Refresh token also expired/invalid — force re-login
        const publicPaths = ['/login', '/forgot-password', '/verify-otp', '/reset-password'];
        if (!publicPaths.includes(window.location.pathname)) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Non-recoverable 401 (session hijacked, role changed, etc.) — go to login
    // Make sure we don't redirect users who are already on public auth pages
    const publicPaths = ['/login', '/forgot-password', '/verify-otp', '/reset-password'];
    if (status === 401 && !publicPaths.includes(window.location.pathname)) {
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export default apiClient;